/**
 * The team profile's Player Stats panel, ISSUE-1384 (v1: teamdetail
 * stats_tab.dart, one table per category). Reads sports-api POST
 * /team/player-stats-get and owns that read, so a fan who never opens the tab
 * never pays for it (Tabs unmounts an inactive panel, see TeamStatsTab.tsx).
 *
 * Each category is the game box score's `BoxScoreTable`: the player column
 * stays put while the stat columns scroll sideways inside the card, so the
 * page never scrolls sideways at phone width, and a rostered player's row
 * opens the player page. The server orders the rows and formats every cell.
 */
import { useTeamPlayerStatsGet } from "@sportsmet/sports-sdk/private";
import { Skeleton } from "@sportsmet/ui";
import { type ReactNode, useEffect } from "react";

import { extractErrorCode, showApiError } from "../../errors/apiErrorBus";
import { BoxScoreTable } from "../gameDetail/BoxScoreTable";
import {
  TEAM_PLAYER_STATS_EMPTY_LINE,
  TEAM_PLAYER_STATS_FAILED_LINE,
  TEAM_PLAYER_STATS_LOADING_LABEL,
  toPlayerStatsTables,
} from "./teamPlayerStatsLogic";
import { TeamReadFailed } from "./TeamReadFailed";

export const TeamPlayerStatsTab = (props: {
  teamId: string;
  teamName: string;
  teamAbbreviation: string;
}): ReactNode => {
  const read = useTeamPlayerStatsGet({ teamId: props.teamId });

  useEffect(() => {
    if (extractErrorCode(read.data) !== null) {
      showApiError(read.data, undefined, read.dataUpdatedAt);
    }
  }, [read.dataUpdatedAt, props.teamId]);

  const failed = (
    <TeamReadFailed
      layout="panel"
      testId="team-player-stats-failed"
      line={TEAM_PLAYER_STATS_FAILED_LINE}
      onRetry={() => {
        void read.refetch();
      }}
    />
  );

  if (extractErrorCode(read.data) !== null) return failed;

  const stats = read.data?.data;
  if (stats === undefined || stats === null) {
    if (read.isPending) {
      return (
        <div data-testid="team-player-stats-loading">
          <Skeleton
            shape="text"
            lines={6}
            label={TEAM_PLAYER_STATS_LOADING_LABEL}
          />
        </div>
      );
    }
    return failed;
  }

  const tables = toPlayerStatsTables(stats.tables);
  if (tables.length === 0) {
    return (
      <p
        data-testid="team-player-stats-empty"
        className="rounded-md border border-hairline bg-surface px-4 py-3 text-body text-ink-secondary"
      >
        {TEAM_PLAYER_STATS_EMPTY_LINE}
      </p>
    );
  }

  const team = {
    teamId: props.teamId,
    name: props.teamName,
    abbreviation: props.teamAbbreviation,
  };
  return (
    <div className="flex flex-col gap-4" data-testid="team-player-stats-tab">
      {tables.map((table) => (
        <BoxScoreTable key={table.key} table={table} team={team} />
      ))}
    </div>
  );
};
